import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { siteVisitService } from '@/services/SiteVisitService'
import { siteVisitKeys } from '@/hooks/queryKeys'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatVisitDay } from '@/site-visits/VisitBar'
import { SiteHistory } from '@/site-visits/SiteHistory'

/**
 * A site's visits, on its row in the client's list of sites: the last one,
 * and its history behind a button. Read-only here; a visit is started and
 * finished from the survey, by whoever is walking the site.
 */
export function SiteVisitsCell({
  siteId,
  siteName,
}: {
  siteId: string
  siteName: string
}) {
  const [open, setOpen] = React.useState(false)
  const query = useQuery({
    queryKey: siteVisitKeys.list(siteId),
    queryFn: () => siteVisitService.list(siteId),
    staleTime: 30_000,
  })
  const visits = query.data?.visits ?? []
  const last = visits[0]

  if (query.isPending) {
    return <span className="text-muted-text">…</span>
  }
  if (last === undefined) {
    return <span className="text-muted-text">None yet</span>
  }

  return (
    <>
      <button
        type="button"
        className="rounded text-left hover:underline focus-visible:outline-2"
        onClick={() => {
          setOpen(true)
        }}
      >
        {formatVisitDay(last.startedOn)}
        <span className="text-muted-text">
          {last.finishedAt === null ? ' · open' : ''}
          {visits.length > 1 ? ` · ${String(visits.length)} visits` : ''}
        </span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>History of {siteName}</DialogTitle>
            <DialogDescription>
              Each visit, and what changed on the site. What a visit found stays
              with it; a new proposal is built from the newest.
            </DialogDescription>
          </DialogHeader>
          {open ? <SiteHistory siteId={siteId} /> : null}
        </DialogContent>
      </Dialog>
    </>
  )
}
